'use client'

import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { ConflictScoreIcon } from '@repo/ui/ConflictScoreIcon'
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro'
import { LockIcon } from '@repo/ui/LockIcon'
import { LockUnlockedIcon } from '@repo/ui/LockUnlockedIcon'
import { Pill } from '@repo/ui/Pill'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { useCOISStore } from '@repo/store/useCOISStore'
import { useEffect, useState } from 'react'
import { useMattersStore } from '@repo/store/useMattersStore'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUIAlert from '@mui/material/Alert'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from './COISPossibleConflicts.module.scss'

export const COISPossibleConflicts = ({ wideMode, handlePrevNext }) => {
  const [conflicts, setConflicts] = useState([])
  const [gotConflicts, setGotConflicts] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedCOISResult, setSelectedCOISResult] = useState(null)
  const [pageSize, setPageSize] = useState(10)
  const [startAtIndex, setStartAtIndex] = useState(0)
  const { fetchConflictsOfInterest, updateParty, currentPartyId, updateCOISWizardSidebar, totalRowCount } =
    useCOISStore()
  const { setMatterDrawerIsOpen } = useMattersStore()
  const apiRef = useGridApiRef()

  useEffect(() => {
    const getConflicts = async () => {
      if (loading) return
      setLoading(true)
      const result = await fetchConflictsOfInterest({ startAtIndex: startAtIndex, pageSize: pageSize })
      setConflicts(result)
      setGotConflicts(true)
      updateParty({ partyId: currentPartyId, newFieldKey: 'conflicts', newFieldValue: result })
      updateCOISWizardSidebar()
      setLoading(false)
    }

    getConflicts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPartyId, fetchConflictsOfInterest, pageSize, startAtIndex])

  useEffect(() => {
    if (selectedCOISResult) {
      const { clientNo, matterNo, scoreReasons, isUserRestricted } = selectedCOISResult?.row || null
      if (isUserRestricted) return
      const clientMatterNo = `${clientNo}-${matterNo}`
      setMatterDrawerIsOpen(true, clientNo, clientMatterNo, scoreReasons)
    } else {
      setMatterDrawerIsOpen(false, null, null, null)
    }
  }, [selectedCOISResult, setMatterDrawerIsOpen])

  const handleDownloadCSV = () => {
    apiRef.current.exportDataAsCsv({
      fileName: 'conflicts',
      hideFooter: true,
      getRowsToExport: ({ apiRef }) => {
        return apiRef.current.getSortedRows().map((row) => row.id)
      }
    })
  }

  const handleClickContinue = () => {
    if (!gotConflicts) return
    updateParty({ partyId: currentPartyId, newFieldKey: 'conflicts', newFieldValue: conflicts })
    updateCOISWizardSidebar()
    handlePrevNext(1)
  }

  const handleClickBack = () => {
    updateCOISWizardSidebar()
    handlePrevNext(-1)
  }

  const paginationModel = { page: startAtIndex / pageSize, pageSize: pageSize }

  return (
    <>
      <MUIStack direction="row" justifyContent="flex-end" spacing={2} sx={{ width: '100%', mb: '16px' }}>
        <MUIButton variant="contained" onClick={handleDownloadCSV}>
          Download CSV
        </MUIButton>
      </MUIStack>
      <WizardPanel data-testid="COISPossibleConflicts" sx={{ height: 'auto', padding: '16px 16px 0' }}>
        {conflicts?.length >= 200 ? (
          <MUIAlert severity="warning" sx={{ width: '100%', mb: 4 }}>
            <MUITypography variant="h6">Heads up!</MUITypography>
            <MUITypography variant="body1">{`You'll find the first ${conflicts.length} matching conflicts below. There could be more and that's a lot to review, so we strongly recommend that you go back and refine your search further. Please note, that if you proceed only the following ${conflicts.length} results will be saved.`}</MUITypography>
          </MUIAlert>
        ) : null}

        <DataGridPro
          apiRef={apiRef}
          rows={conflicts}
          columns={columns}
          rowHeight={54}
          rowSelection
          cellSelection={false}
          disableColumnSorting
          disableColumnMenu
          disableColumnResize
          loading={loading}
          onPaginationModelChange={(params) => {
            setStartAtIndex(params.page * params.pageSize)
            setPageSize(params.pageSize)
          }}
          onRowClick={(params) => setSelectedCOISResult(params)}
          pageSizeOptions={[5, 10, 25, 50]}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          rowCount={totalRowCount}
          slotProps={{ loadingOverlay: { noRowsVariant: 'skeleton' } }}
          initialState={{ sorting: { sortModel: [{ field: 'score', sort: 'desc' }] } }}
          sx={{
            border: 'none',
            fontSize: '14px',
            maxWidth: '100%',
            '.MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
            '.MuiDataGrid-columnHeader:focus': { outline: 'none' },
            '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '.MuiDataGrid-row': {
              cursor: 'pointer',
              borderBottomColor: 'rgba(5, 28, 51, 0.12)',
              '&:hover': { backgroundColor: 'rgba(61, 81, 123, 0.04)' },
              '&.Mui-selected': { backgroundColor: 'rgba(61, 81, 123, 0.08)' }
            },
            '.MuiTablePagination-selectLabel': { display: 'block !important' },
            '.MuiTablePagination-input': {
              display: 'inline-flex !important',
              mb: 0,
              ml: 0,
              mt: '8px',
              fontSize: '0.875rem',
              '.MuiSvgIcon-root': { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
            },
            '.MuiTablePagination-select': {
              padding: '0 !important',
              ml: '4px',
              mr: '20px',
              minWidth: 'none',
              [`&:focus`]: { backgroundColor: 'transparent' }
            },
            '.MuiTablePagination-actions': { mt: '10px', '& .MuiButtonBase-root': { padding: '0' } }
          }}
        />
      </WizardPanel>
      <WizardFooter
        wideMode={wideMode}
        handleClickContinue={handleClickContinue}
        handleClickBack={handleClickBack}
        continueButtonDisabled={!gotConflicts}
      />
    </>
  )
}

COISPossibleConflicts.propTypes = { wideMode: PropTypes.bool, handlePrevNext: PropTypes.func }

const columns = [
  {
    field: 'clientNo',
    headerName: 'Matter',
    headerClassName: styles.tableHeader,
    width: 120,
    valueGetter: (value, row) => `${row.clientNo || ''}-${row.matterNo || ''}`,
    renderCell: (params) => (
      <ClientMatterDetailsPill
        clientNo={params.row.clientNo}
        matterNo={params.row.matterNo}
        sx={{ height: '22px' }}
        isUserRestricted={params.row.isUserRestricted}
      />
    )
  },
  {
    field: 'isUserRestricted',
    headerName: 'Access',
    headerClassName: styles.tableHeader,
    width: 80,
    renderCell: (params) => (
      <MUITooltip
        title={params.value ? 'You are not authorised to view this matter' : 'You are authorised to view this matter'}
        placement="top"
        arrow
      >
        {params.value ? (
          <MUIStack alignItems="center" justifyContent="center" direction="row" style={{ width: '100%' }}>
            <LockIcon sx={{ width: '18px', height: '18px' }} />
          </MUIStack>
        ) : (
          <MUIStack alignItems="center" justifyContent="center" direction="row" style={{ width: '100%' }}>
            <LockUnlockedIcon sx={{ width: '18px', height: '18px' }} />
          </MUIStack>
        )}
      </MUITooltip>
    )
  },
  { field: 'clientName', headerName: 'Client name', headerClassName: styles.tableHeader, flex: 1 },
  {
    field: 'matterDescription',
    headerName: 'Matter description',
    headerClassName: styles.tableHeader,
    flex: 1,
    renderCell: (params) => <TextLinkButton title={params.value} sx={{ textAlign: 'left' }} />
  },
  {
    field: 'workflowTypeDescription',
    headerName: 'Work type',
    headerClassName: styles.tableHeader,
    flex: 1,
    renderCell: (params) => <Pill label={params.value} backgroundColor="background.mid" />
  },

  {
    field: 'score',
    headerName: 'Conflict score',
    headerClassName: styles.tableHeader,
    width: 150,
    renderCell: (params) => (
      <MUITooltip title={params?.row?.scoreReasons?.join('. ') || ''} placement="top" arrow>
        <span>
          <ConflictScoreIcon sx={{ width: '56px', height: '14px' }} score={params.value} />
        </span>
      </MUITooltip>
    )
  }
]
